import { useState, useCallback, useEffect, useRef } from "react";
import {
  useRealtimeKitMeeting,
  useRealtimeKitSelector,
} from "@cloudflare/realtimekit-react";
import { Users } from "lucide-react";
import { MeetingLogoutButton } from "./MeetingLogoutButton";
import { LivestreamHostSidebar } from "./LivestreamHostSidebar";
import { LivestreamHostPreviewVideo } from "./LivestreamHostPreviewVideo";
import { LivestreamVideoGrid } from "./LivestreamVideoGrid";
import { LivestreamRandomParticipantOverlay } from "./LivestreamRandomParticipantOverlay";
import { LivestreamPinnedCommentOverlay } from "./LivestreamPinnedCommentOverlay";
import { LivestreamViewerFrameOverlay } from "./LivestreamViewerFrameOverlay";
import { useLivestreamChat } from "./useLivestreamChat";
import { useLivestreamPinnedComment } from "./useLivestreamPinnedComment";
import { useLivestreamRandomParticipant } from "./useLivestreamRandomParticipant";
import { isHostParticipant } from "./livestreamStageFeeds";
import { useLivestreamStageFeeds, useOtherHostLivestreamActive } from "./useLivestreamStageFeeds";
import { useLivestreamRequestJoinVisibility } from "./useLivestreamRequestJoinVisibility";
import { useLivestreamStageHost } from "./useLivestreamStageHost";
import { useHostMediaLocalRecording } from "./useHostMediaLocalRecording";
import { useHostMediaStream, isMediaTrackLive } from "./useHostMediaStream";
import type { MeetingShopTipping } from "./meetingProductTypes";

export function LivestreamHostView({
  shopProductIds,
  shopTipping,
  showLogout,
  loggingOut,
  onLogout,
  productName,
  baseUrl,
}: {
  shopProductIds: string[];
  shopTipping?: MeetingShopTipping;
  showLogout: boolean;
  loggingOut: boolean;
  onLogout: () => void;
  productName?: string;
  baseUrl?: string;
}) {
  const { meeting } = useRealtimeKitMeeting();
  const selfId = useRealtimeKitSelector((m) => m.self.id);
  const selfName = useRealtimeKitSelector((m) => m.self.name);
  const stageStatus = useRealtimeKitSelector((m) => m.stage.status);
  const onStage = stageStatus === "ON_STAGE";
  const roomState = useRealtimeKitSelector((m) => m.self.roomState);
  const videoEnabled = useRealtimeKitSelector((m) => m.self.videoEnabled);
  const audioEnabled = useRealtimeKitSelector((m) => m.self.audioEnabled);
  const screenShareEnabled = useRealtimeKitSelector((m) => m.self.screenShareEnabled);
  const canShareScreen =
    useRealtimeKitSelector((m) => m.self.permissions.canProduceScreenshare) === "ALLOWED";
  const canRecord = useRealtimeKitSelector((m) => m.self.permissions.canRecord);
  const canKickParticipants = useRealtimeKitSelector(
    (m) => m.self.permissions.kickParticipant,
  );
  const recordingState = useRealtimeKitSelector((m) => m.recording.recordingState);
  const isRecordingActive =
    recordingState === "RECORDING" ||
    recordingState === "PAUSED" ||
    recordingState === "STARTING";
  const recordingBusy =
    recordingState === "STARTING" || recordingState === "STOPPING";
  const showLogoutButton = showLogout && roomState !== "joined";
  const [isLive, setIsLive] = useState(false);
  const [goingLive, setGoingLive] = useState(false);
  const [endingStream, setEndingStream] = useState(false);
  const [joiningStage, setJoiningStage] = useState(false);
  const [leavingStage, setLeavingStage] = useState(false);
  const coHostStageInitializedRef = useRef(false);
  const [previewVideoEnabled, setPreviewVideoEnabled] = useState(true);
  const [previewAudioEnabled, setPreviewAudioEnabled] = useState(true);
  const publishAudioWhenReadyRef = useRef(false);
  const publishVideoWhenReadyRef = useRef(false);
  const endingStreamRef = useRef(false);
  const { videoRef, videoTrack, audioTrack, refreshMediaStream } = useHostMediaStream({
    meeting,
    previewVideoEnabled: !isLive && previewVideoEnabled,
  });
  const { messages, sendMessage } = useLivestreamChat({ meeting });
  const { pinnedComment, togglePinComment } = useLivestreamPinnedComment({
    meeting,
    isHost: true,
    messages,
  });
  const hasShopProducts = shopProductIds.length > 0;
  const {
    requestJoinVisible,
    setRequestJoinVisible,
    shopVisible,
    setShopVisible,
  } = useLivestreamRequestJoinVisibility({
    meeting,
    isHost: true,
  });
  const otherHostIsLive = useOtherHostLivestreamActive({ meeting, selfId });
  const isCoHost = otherHostIsLive && !isLive;
  const stageFeeds = useLivestreamStageFeeds({
    meeting,
    includeSelf: isLive || (isCoHost && onStage),
  });
  const {
    participants,
    stageRequests,
    grantAccess,
    denyAccess,
    removeFromStage,
    kickFromMeeting,
    actingOnUserId,
  } = useLivestreamStageHost({ meeting });
  const { activeDraw, clearActiveDraw, selectRandomParticipant } =
    useLivestreamRandomParticipant({ meeting });
  const { prepareRecordingDestination, startHostRecording, stopHostRecording } =
    useHostMediaLocalRecording({
    meeting,
    videoTrack,
    audioTrack,
    productName,
  });
  const [selectingRandomParticipant, setSelectingRandomParticipant] = useState(false);
  const [randomWinnerName, setRandomWinnerName] = useState<string | null>(null);
  const eligibleRandomParticipantNames = participants
    .filter((participant) => !participant.isSelf && !participant.isHost)
    .map((participant) => participant.name);
  const hasStageScreenShare = stageFeeds.some(
    (participant) =>
      participant.screenShareEnabled && Boolean(participant.screenShareTracks?.video),
  );
  const showViewerFrameOverlay =
    (isLive || isCoHost) && stageFeeds.length === 1 && !hasStageScreenShare;
  const participantCount = participants.length;
  const showStreamGrid = isLive || isCoHost;

  useEffect(() => {
    if (!meeting || !isCoHost || coHostStageInitializedRef.current) return;

    coHostStageInitializedRef.current = true;
    void (async () => {
      if (meeting.stage.status === "REQUESTED_TO_JOIN_STAGE") {
        await meeting.stage.cancelRequestAccess();
      }
      if (meeting.stage.status === "ON_STAGE") {
        if (meeting.self.audioEnabled) await meeting.self.disableAudio();
        if (meeting.self.videoEnabled) await meeting.self.disableVideo();
        await meeting.stage.leave();
      }
    })();
  }, [isCoHost, meeting]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isLive) return;
      const warning =
        "Leaving will end the livestream and you may lose your recording.";
      event.preventDefault();
      event.returnValue = warning;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isLive]);

  useEffect(() => {
    if (!meeting || !isLive || endingStreamRef.current) return;

    void (async () => {
      if (audioTrack && (audioEnabled || publishAudioWhenReadyRef.current)) {
        publishAudioWhenReadyRef.current = false;
        await meeting.self.enableAudio(audioTrack);
      }
      if (videoTrack && (videoEnabled || publishVideoWhenReadyRef.current)) {
        publishVideoWhenReadyRef.current = false;
        await meeting.self.enableVideo(videoTrack);
      }
    })();
  }, [meeting, isLive, audioEnabled, videoEnabled, audioTrack, videoTrack]);

  const ensureHostOnStage = useCallback(async () => {
    if (!meeting) return;

    if (meeting.stage.status === "ON_STAGE") return;

    if (meeting.stage.status === "OFF_STAGE") {
      await meeting.stage.requestAccess();
    }

    if (meeting.stage.status === "ACCEPTED_TO_JOIN_STAGE") {
      await meeting.stage.join();
    }
  }, [meeting]);

  const removeOtherHostsFromStage = useCallback(async () => {
    if (!meeting?.stage) return;

    const selfId = meeting.self.id;
    const otherHostsOnStage = meeting.participants.joined
      .toArray()
      .filter(
        (participant) =>
          participant.id !== selfId &&
          isHostParticipant({ participant }) &&
          participant.stageStatus === "ON_STAGE",
      );

    if (otherHostsOnStage.length > 0) {
      await meeting.stage.kick(otherHostsOnStage.map((participant) => participant.userId));
    }
  }, [meeting]);

  const startRecording = useCallback(
    async ({
      videoTrack: recordingVideoTrack,
      audioTrack: recordingAudioTrack,
    }: {
      videoTrack?: MediaStreamTrack | null;
      audioTrack?: MediaStreamTrack | null;
    } = {}) => {
      if (!meeting || !canRecord || recordingBusy || isRecordingActive) return false;
      const started = await startHostRecording({
        videoTrack: recordingVideoTrack,
        audioTrack: recordingAudioTrack,
      });
      if (started) {
        void meeting.recording.start();
      }
      return started;
    },
    [canRecord, isRecordingActive, meeting, recordingBusy, startHostRecording],
  );

  const handleGoLive = useCallback(async () => {
    if (!meeting || goingLive || isLive) return;
    setGoingLive(true);
    try {
      if (canRecord) {
        const recordingDestinationReady = await prepareRecordingDestination();
        if (!recordingDestinationReady) return;
      }

      await ensureHostOnStage();
      await removeOtherHostsFromStage();

      let activeAudioTrack = audioTrack;
      let activeVideoTrack = videoTrack;
      if (!isMediaTrackLive(activeAudioTrack) || !isMediaTrackLive(activeVideoTrack)) {
        const refreshed = await refreshMediaStream();
        activeAudioTrack = refreshed.audioTrack;
        activeVideoTrack = refreshed.videoTrack;
      }

      publishAudioWhenReadyRef.current = previewAudioEnabled;
      publishVideoWhenReadyRef.current = previewVideoEnabled;
      if (previewAudioEnabled && activeAudioTrack) {
        publishAudioWhenReadyRef.current = false;
        await meeting.self.enableAudio(activeAudioTrack);
      }
      if (previewVideoEnabled && activeVideoTrack) {
        publishVideoWhenReadyRef.current = false;
        await meeting.self.enableVideo(activeVideoTrack);
      }
      await startRecording({
        videoTrack: activeVideoTrack,
        audioTrack: activeAudioTrack,
      });
      setIsLive(true);
    } finally {
      setGoingLive(false);
    }
  }, [
    audioTrack,
    canRecord,
    ensureHostOnStage,
    goingLive,
    removeOtherHostsFromStage,
    isLive,
    meeting,
    prepareRecordingDestination,
    previewAudioEnabled,
    previewVideoEnabled,
    refreshMediaStream,
    startRecording,
    videoTrack,
  ]);

  const handleJoinStage = useCallback(async () => {
    if (!meeting || joiningStage || !isCoHost || onStage) return;

    setJoiningStage(true);
    try {
      if (meeting.stage.status === "OFF_STAGE") {
        await meeting.stage.requestAccess();
      }
      if (meeting.stage.status === "ACCEPTED_TO_JOIN_STAGE") {
        await meeting.stage.join();
      }

      let activeAudioTrack = audioTrack;
      let activeVideoTrack = videoTrack;
      if (!isMediaTrackLive(activeAudioTrack) || !isMediaTrackLive(activeVideoTrack)) {
        const refreshed = await refreshMediaStream();
        activeAudioTrack = refreshed.audioTrack;
        activeVideoTrack = refreshed.videoTrack;
      }
      if (previewAudioEnabled && activeAudioTrack) {
        await meeting.self.enableAudio(activeAudioTrack);
      }
      if (previewVideoEnabled && activeVideoTrack) {
        await meeting.self.enableVideo(activeVideoTrack);
      }
    } finally {
      setJoiningStage(false);
    }
  }, [
    audioTrack,
    isCoHost,
    joiningStage,
    meeting,
    onStage,
    previewAudioEnabled,
    previewVideoEnabled,
    refreshMediaStream,
    videoTrack,
  ]);

  const handleLeaveStage = useCallback(async () => {
    if (!meeting || leavingStage || !onStage) return;

    setLeavingStage(true);
    try {
      if (meeting.self.audioEnabled) await meeting.self.disableAudio();
      if (meeting.self.videoEnabled) await meeting.self.disableVideo();
      if (meeting.self.screenShareEnabled) await meeting.self.disableScreenShare();
      await meeting.stage.leave();
      await refreshMediaStream();
    } finally {
      setLeavingStage(false);
    }
  }, [leavingStage, meeting, onStage, refreshMediaStream]);

  const handleEndStream = useCallback(async () => {
    if (!meeting || endingStream || !isLive) return;
    setEndingStream(true);
    endingStreamRef.current = true;
    try {
      if (isRecordingActive) {
        stopHostRecording();
        await meeting.recording.stop();
      }
      publishAudioWhenReadyRef.current = false;
      publishVideoWhenReadyRef.current = false;
      if (screenShareEnabled) {
        await meeting.self.disableScreenShare();
      }
      if (audioEnabled) {
        await meeting.self.disableAudio();
      }
      if (videoEnabled) {
        await meeting.self.disableVideo();
      }
      if (meeting.stage.status === "ON_STAGE") {
        await meeting.stage.leave();
      }
      setIsLive(false);
      await refreshMediaStream();
    } finally {
      endingStreamRef.current = false;
      setEndingStream(false);
    }
  }, [
    audioEnabled,
    endingStream,
    isLive,
    isRecordingActive,
    meeting,
    refreshMediaStream,
    screenShareEnabled,
    stopHostRecording,
    videoEnabled,
  ]);

  if (!meeting) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-black text-white">
        Connecting...
      </div>
    );
  }

  return (
    <div className="flex h-dvh w-screen bg-black">
      <div className="@container relative min-w-0 flex-1 [container-type:size]">
        {showLogoutButton ? (
          <MeetingLogoutButton
            showLogout={showLogoutButton}
            loggingOut={loggingOut}
            onLogout={onLogout}
          />
        ) : null}
        {participantCount > 0 ? (
          <div
            className="pointer-events-none absolute top-[max(0.75rem,env(safe-area-inset-top))] left-3 z-20 flex items-center gap-1.5 rounded-full border border-white/20 bg-black/55 px-2.5 py-1 text-sm text-white backdrop-blur-sm sm:left-4"
            aria-label={`${participantCount} participant${participantCount === 1 ? "" : "s"}`}
          >
            <Users className="h-3.5 w-3.5" aria-hidden />
            <span>{participantCount}</span>
          </div>
        ) : null}
        <div className="relative h-full w-full">
          <LivestreamHostPreviewVideo
            videoEnabled={previewVideoEnabled}
            name={selfName ?? ""}
            videoRef={videoRef}
            mirrored
            className={showStreamGrid ? "invisible" : undefined}
          />
          {showStreamGrid ? (
            <div className="absolute inset-0">
              <LivestreamVideoGrid
                participants={stageFeeds}
                selfId={selfId}
                mirrorSelf={isLive || onStage}
              />
            </div>
          ) : null}
        </div>
        <LivestreamRandomParticipantOverlay draw={activeDraw} onComplete={clearActiveDraw} />
        <LivestreamPinnedCommentOverlay
          pinnedComment={pinnedComment}
          className="bottom-[max(0.75rem,env(safe-area-inset-bottom))] sm:bottom-[max(0.75rem,env(safe-area-inset-bottom))]"
        />
        {showViewerFrameOverlay ? <LivestreamViewerFrameOverlay /> : null}
      </div>
      <LivestreamHostSidebar
        baseUrl={baseUrl}
        isCoHost={isCoHost}
        onStage={onStage}
        joiningStage={joiningStage}
        leavingStage={leavingStage}
        onJoinStage={handleJoinStage}
        onLeaveStage={handleLeaveStage}
        messages={messages}
        onSendMessage={sendMessage}
        pinnedComment={pinnedComment}
        onTogglePinComment={togglePinComment}
        participants={participants}
        stageRequests={stageRequests}
        onGrantStageAccess={async ({ userId }) => {
          if (hasStageScreenShare) return;
          await grantAccess({ userId });
        }}
        onDenyStageAccess={denyAccess}
        onRemoveFromStage={removeFromStage}
        onKickFromMeeting={kickFromMeeting}
        onSelectRandomParticipant={async () => {
          if (selectingRandomParticipant || eligibleRandomParticipantNames.length === 0) return;
          setSelectingRandomParticipant(true);
          try {
            const winner = await selectRandomParticipant({
              eligibleNames: eligibleRandomParticipantNames,
            });
            if (winner) setRandomWinnerName(winner);
          } finally {
            setSelectingRandomParticipant(false);
          }
        }}
        selectingRandomParticipant={selectingRandomParticipant}
        canSelectRandomParticipant={eligibleRandomParticipantNames.length > 0}
        randomWinnerName={randomWinnerName}
        actingOnUserId={actingOnUserId}
        canGrantStageAccess={!hasStageScreenShare}
        canKickParticipants={canKickParticipants}
        isLive={isLive}
        goingLive={goingLive}
        endingStream={endingStream}
        onGoLive={handleGoLive}
        onEndStream={handleEndStream}
        screenShareEnabled={screenShareEnabled}
        canShareScreen={canShareScreen && (isLive || (isCoHost && onStage))}
        isRecordingActive={isRecordingActive}
        recordingBusy={recordingBusy}
        onToggleScreenShare={() => {
          if (!isLive && !(isCoHost && onStage)) return;
          if (screenShareEnabled) {
            void meeting.self.disableScreenShare();
          } else {
            void meeting.self.enableScreenShare();
          }
        }}
        requestJoinVisible={requestJoinVisible}
        onRequestJoinVisibleChange={({ visible }) => {
          void setRequestJoinVisible({ visible });
        }}
        hasShopProducts={hasShopProducts}
        shopTipping={shopTipping}
        shopVisible={shopVisible}
        onShopVisibleChange={({ visible }) => {
          void setShopVisible({ visible });
        }}
      />
    </div>
  );
}
